import { useState } from "react";
import { useSEO } from "@/hooks/useSEO";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6bn1vqhq9pk5mecs80";

const values = [
  {
    icon: "ri-play-circle-line",
    title: "内容优先",
    text: "坚持筛选有质量、有温度的短视频，让每一次点击都不虚此行。",
  },
  {
    icon: "ri-compass-3-line",
    title: "发现有趣",
    text: "用清晰的分类与智能推荐，帮你在碎片时间里轻松遇见喜欢的内容。",
  },
  {
    icon: "ri-share-forward-line",
    title: "乐于分享",
    text: "一键分享精彩瞬间，让好内容被更多人看见，让快乐被传递得更远。",
  },
];

export default function About() {
  useSEO({
    title: "关于我们 - 糖豆视频",
    description:
      "了解糖豆视频的定位、团队与发展愿景。糖豆视频致力于打造便捷友好的短视频分享与观看平台，欢迎通过邮箱、客服电话或反馈表单联系我们。",
    keywords: "关于糖豆视频,糖豆视频团队,联系方式,意见反馈,短视频平台",
  });

  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("sending");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查后稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后再试。");
    }
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-12 pb-10 bg-background-100">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-700">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-emotion-happy-line"></i>
            </span>
            关于我们
          </span>
          <h1 className="mt-3 text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
            让每一段碎片时间，都有糖豆视频陪着
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
            糖豆视频是一个聚焦优质短视频内容分享与观看的平台，覆盖娱乐、生活、趣味、美食、旅行、科技、音乐、运动等类别。我们相信，短短几分钟也能带来真实的快乐与灵感，于是把发现好内容这件事，做得更简单一点。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {values.map((item) => (
            <div
              key={item.title}
              className="p-6 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-2xl">
                <i className={item.icon}></i>
              </span>
              <h2 className="mt-4 text-base font-heading font-semibold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-secondary-100">
            <img
              src="https://readdy.ai/api/search-image?query=Friendly%20creative%20team%20collaborating%20around%20a%20table%20with%20laptops%20and%20sticky%20notes%20in%20a%20bright%20modern%20studio%2C%20warm%20natural%20light%2C%20relaxed%20positive%20atmosphere%2C%20editorial%20business%20photography&width=800&height=600&seq=td-about-team&orientation=landscape"
              alt="糖豆视频团队"
              title="糖豆视频 团队简介"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              团队与发展愿景
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              糖豆视频的团队由一群热爱内容、也热爱生活的人组成。有做过纪录片的内容策划，也有敲了多年的工程师，还有每天都在刷视频的产品同学——我们既是创作者，也是最挑剔的观众。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              未来我们希望把平台做得更懂你：更准确的推荐、更顺畅的观看体验，还有更多普通人被看见的机会。让好的内容不被埋没，也让每个人都能在这里找到属于自己的那一份乐趣。
            </p>
            <div className="mt-7 grid grid-cols-3 gap-4">
              <div>
                <div className="text-2xl font-heading font-bold text-primary-600">40+</div>
                <div className="mt-1 text-xs text-foreground-500">精选视频</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-primary-600">8</div>
                <div className="mt-1 text-xs text-foreground-500">内容分类</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-primary-600">6 年</div>
                <div className="mt-1 text-xs text-foreground-500">内容深耕</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-heading font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              无论是内容合作、反馈建议还是遇到问题，都欢迎随时找我们聊聊，我们会在两个工作日内回复。
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <div className="text-sm text-foreground-500">合作邮箱</div>
                  <div className="text-base font-medium text-foreground-900">hello@tangdouvideo.com</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl shrink-0">
                  <i className="ri-phone-line"></i>
                </span>
                <div>
                  <div className="text-sm text-foreground-500">客服电话</div>
                  <div className="text-base font-medium text-foreground-900">400-800-2026</div>
                  <div className="text-xs text-foreground-400">服务时间 09:00 - 21:00</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl shrink-0">
                  <i className="ri-map-pin-line"></i>
                </span>
                <div>
                  <div className="text-sm text-foreground-500">办公地址</div>
                  <div className="text-base font-medium text-foreground-900">上海市杨浦区创智天地 3 号楼</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
              <h2 className="text-xl font-heading font-bold text-foreground-950">意见反馈</h2>
              <p className="mt-2 text-sm text-foreground-500">
                你的每一条建议，我们都会认真读完。
              </p>

              {status === "success" ? (
                <div className="mt-6 flex flex-col items-center text-center py-8">
                  <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-3xl">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">
                    反馈已发送，感谢你的支持！
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500">
                    我们会尽快阅读你的建议，如有需要会通过邮箱与你联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="about-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
                        你的称呼
                      </label>
                      <input
                        id="fb-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入姓名或昵称"
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                      />
                    </div>
                    <div>
                      <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="fb-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-1.5">
                      反馈类型
                    </label>
                    <select
                      id="fb-topic"
                      name="topic"
                      defaultValue="建议"
                      className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
                    >
                      <option value="建议">功能建议</option>
                      <option value="内容">内容相关</option>
                      <option value="问题">使用问题</option>
                      <option value="合作">商务合作</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
                      你的意见
                    </label>
                    <textarea
                      id="fb-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你遇到的问题或想给我们的建议（最多 500 字）"
                      className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="about-field-note"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="flex items-center gap-1.5 text-sm text-accent-700">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-error-warning-line"></i>
                      </span>
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={status === "sending" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    </span>
                    {status === "sending" ? "发送中…" : "提交反馈"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}